/**
 * Capacitor native shell helpers.
 * Dev builds load http://localhost:8080 (or LAN IP) and auto-reload when bundle.js changes.
 */
import { Capacitor } from '@capacitor/core';

const DEV_BUNDLE_VERSION_PATH = '/static/frontend/bundle.version';
const DEV_RELOAD_POLL_MS = 2500;

export const isNativeApp = () => Capacitor.isNativePlatform();

export const nativePlatform = () => Capacitor.getPlatform();

/** Call before React render so theme/layout can detect native shell synchronously. */
export function markNativeShellSync() {
  if (!Capacitor.isNativePlatform() || typeof document === 'undefined') {
    return;
  }
  const platformClass = `capacitor-${Capacitor.getPlatform()}`;
  document.documentElement.classList.add('capacitor-native', platformClass, 'mobile-shell-theme', 'theme-dark');
  document.body.classList.add(platformClass, 'mobile-shell-theme', 'theme-dark');
  document.documentElement.style.backgroundColor = '#030712';
  document.body.style.backgroundColor = '#030712';
  document.documentElement.style.height = '100%';
  document.body.style.height = '100%';

  if (!document.getElementById('capacitor-native-fixes')) {
    const style = document.createElement('style');
    style.id = 'capacitor-native-fixes';
    style.textContent = `
      #djDebug, #djDebugToolbar, #djdt-ToolbarHandle { display: none !important; }
      html.capacitor-native, html.capacitor-native body, html.capacitor-native #root {
        min-height: 100vh;
        height: 100%;
      }
      /* Prefer compositor-friendly scrolling on Android WebView */
      html.capacitor-android {
        -webkit-tap-highlight-color: transparent;
      }
    `;
    document.head.appendChild(style);
  }

  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (viewportMeta) {
    const iosViewport =
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
    const defaultViewport =
      'width=device-width, initial-scale=1, shrink-to-fit=no, viewport-fit=cover';
    viewportMeta.setAttribute(
      'content',
      Capacitor.getPlatform() === 'ios' ? iosViewport : defaultViewport
    );
  }
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', '#030712');
  }
}

function isDevServerHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return (
    host === 'localhost'
    || host === '127.0.0.1'
    || host === '10.0.2.2'
    || /^192\.168\./.test(host)
    || /^10\./.test(host)
  );
}

function startNativeDevAutoReload() {
  if (!Capacitor.isNativePlatform() || !isDevServerHost()) {
    return;
  }

  let lastBundleVersion = '';

  const poll = async () => {
    try {
      // Only poll the tiny version stamp — never HEAD the multi‑MB bundle.js on device.
      const versionResponse = await fetch(`${DEV_BUNDLE_VERSION_PATH}?t=${Date.now()}`, {
        method: 'GET',
        cache: 'no-store',
      });
      if (!versionResponse.ok) return;
      const bundleVersion = (await versionResponse.text()).trim();
      if (lastBundleVersion && bundleVersion && lastBundleVersion !== bundleVersion) {
        window.location.reload();
        return;
      }
      if (bundleVersion) {
        lastBundleVersion = bundleVersion;
      }
    } catch {
      // Dev server may still be starting.
    }
  };

  poll();
  window.setInterval(poll, DEV_RELOAD_POLL_MS);
}

async function ignoreUnimplemented(nativeCall) {
  try {
    await nativeCall();
  } catch (error) {
    if (error?.code !== 'UNIMPLEMENTED') {
      throw error;
    }
  }
}

const TABLET_MIN_SHORT_SIDE = 600;
const TABLET_MIN_LONG_SIDE = 900;

/** Phone stays portrait; tablets (7"/10" Android, iPad) may rotate like iPadOS. */
export function isNativeTabletDevice() {
  if (!Capacitor.isNativePlatform() || typeof window === 'undefined') {
    return false;
  }

  const platform = Capacitor.getPlatform();
  const ua = navigator.userAgent || '';

  if (platform === 'ios') {
    if (/iPad/i.test(ua)) {
      return true;
    }
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
      return true;
    }
  }

  if (platform === 'android' && /Android/i.test(ua) && !/Mobile/i.test(ua)) {
    return true;
  }

  const w = window.screen?.width ?? window.innerWidth;
  const h = window.screen?.height ?? window.innerHeight;
  const minSide = Math.min(w, h);
  const maxSide = Math.max(w, h);
  return minSide >= TABLET_MIN_SHORT_SIDE && maxSide >= TABLET_MIN_LONG_SIDE;
}

/** Phones: portrait lock. Tablets: allow rotation (matches iPad behavior). */
export async function ensurePortraitOrientation() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const { ScreenOrientation } = await import('@capacitor/screen-orientation');
    if (isNativeTabletDevice()) {
      await ScreenOrientation.unlock();
      return;
    }
    await ScreenOrientation.lock({ orientation: 'portrait' });
  } catch (error) {
    if (error?.code !== 'UNIMPLEMENTED') {
      console.warn('Orientation policy skipped:', error);
    }
  }
}

export async function initializeCapacitorShell() {
  markNativeShellSync();
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const [{ App }, { StatusBar, Style }] = await Promise.all([
      import('@capacitor/app'),
      import('@capacitor/status-bar'),
    ]);

    await ensurePortraitOrientation();

    await StatusBar.setStyle({ style: Style.Light });
    if (Capacitor.getPlatform() === 'android') {
      await ignoreUnimplemented(() => StatusBar.setBackgroundColor({ color: '#030712' }));
    }
    if (Capacitor.getPlatform() === 'ios') {
      await ignoreUnimplemented(() => StatusBar.setOverlaysWebView({ overlay: false }));
    }

    // Keyboard resize/scroll behavior is configured in capacitor.config.js.
    // Avoid runtime keyboard mutation on iOS: certain simulator/runtime builds
    // can suppress or destabilize software keyboard presentation when these are
    // called after the WebView is already active.

    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        ensurePortraitOrientation();
      }
    });

    startNativeDevAutoReload();
  } catch (error) {
    console.warn('Capacitor shell init skipped:', error);
  }
}

export default {
  isNativeApp,
  nativePlatform,
  markNativeShellSync,
  isNativeTabletDevice,
  ensurePortraitOrientation,
  initializeCapacitorShell,
};
